import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ILocation } from '../../Interfaces/ILocation';
import { customFetcher } from '../useFetch';

const mapLocationPayload = (location: Partial<ILocation>) => {
  const { idPostcode, StreetAdditional, Comment, ...rest } = location;

  return {
    ...rest,
    Postcode_idPostcode: idPostcode,
    StreetAdditional: StreetAdditional ?? '',
    Comment: Comment ?? '',
  };
};

export const useGetLocations = () => {
  const {
    data: locations,
    isLoading: isLoadingLocations,
    refetch: refetchLocations,
  } = useQuery<ILocation[]>({
    queryKey: ['location'],
    queryFn: () => customFetcher({ endpoint: '/location' }),
    refetchOnWindowFocus: false,
  });

  return { locations, isLoadingLocations, refetchLocations };
};

export const useCreateLocation = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: createLocation, isPending: isPendingCreateLocation } =
    useMutation({
      mutationFn: (location: Omit<ILocation, 'idLocation'>) =>
        customFetcher({
          endpoint: '/location',
          method: 'POST',
          body: mapLocationPayload(location),
        }),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['location'] });
      },
    });

  return { createLocation, isPendingCreateLocation };
};

export const useUpdateLocation = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateLocation, isPending: isPendingUpdateLocation } =
    useMutation({
      mutationFn: (location: ILocation) =>
        customFetcher({
          endpoint: '/location',
          method: 'PUT',
          body: mapLocationPayload(location),
        }),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['location'] });
      },
    });

  return { updateLocation, isPendingUpdateLocation };
};

export const useDeleteLocation = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: deleteLocation, isPending: isPendingDeleteLocation } =
    useMutation({
      mutationFn: (idLocation: number) =>
        customFetcher({
          endpoint: `/location/${idLocation}`,
          method: 'DELETE',
        }),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['location'] });
      },
    });

  return { deleteLocation, isPendingDeleteLocation };
};

export const useSetDefaultLocation = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: setDefaultLocation,
    isPending: isPendingSetDefaultLocation,
  } = useMutation({
    mutationFn: (idLocation: number) =>
      customFetcher({
        endpoint: `/location/${idLocation}/setDefault`,
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['location'] });
    },
  });

  return { setDefaultLocation, isPendingSetDefaultLocation };
};
